import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormEvent, ReactNode } from 'react';
import styled, { css } from 'styled-components';

export const StyledTicketHeader = styled.div`
    display: flex;
    justify-content: space-between;
    margin: 20px 0;
`;

export const StyledHeading = styled.h1`
    padding: 0;
`;

export const StyledTicketCount = styled.span`
    font-size: 16px;
    margin-left: 6px;
`;

export const StyledButtonGroup = styled.div`
    display: flex;
    .gi-button:last-child {
        margin-right: 0.5rem;
    }

    button:not([disabled]):focus {
        outline: none;
    }
`;

export const StyledClaimNewTicketButton = styled.div`
    .gi-button:last-child {
        margin-right: 0;
    }

    .gi-button svg {
        margin-right: 0;
    }
`;

export const StyledWorkgroupList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 8px 0;

    li:hover {
        background-color: ${({ theme }) => theme.gray10};
    }
`;

export const StyledWorkgroupListItem = styled.li`
    padding: 4px 8px;
    display: flex;
    align-items: center;
    cursor: pointer;
`;

export const StyledHeaderButtonGroup = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-bottom: 1rem;
`;

export const StyledDownloadButton = styled.div`
    margin-left: auto;
    .usa-button {
        margin-right: 0;
    }

    .gi-button svg {
        margin-right: 0;
    }
`;

export const StyledTabWrapper = styled.div`
    display: flex;
    gap: 10px;
    margin-bottom: 6px;
`;

export const StyledButton = styled.div`
    width: 100%;
`;

export const StyledTab = styled.span<{
    onClick: () => void;
    children: ReactNode;
    disabled?: boolean;
}>`
    color: ${({ theme, disabled }) => (disabled ? theme.grayLight : theme.primaryDark)};
    cursor: pointer;
`;

export const StyledTabButton = styled.button`
    color: ${({ theme }) => theme.primaryDark};
    cursor: pointer;
    border: none;
    background: none;
    padding: 0;
    margin: 0;
    font: inherit;
    text-align: inherit;
    text-transform: inherit;

    &[aria-selected='true'] {
        color: inherit;
        cursor: default;
    }
`;

export const StyledContentCard = styled.div`
    box-sizing: border-box;
    border: 1px solid #dddde5;
    border-radius: 8px;
    padding: 24px 24px 32px 24px;
    margin-bottom: 24px;
    background-color: ${({ theme }) => theme.defaultWhite};
    transition: 0.1s;
    box-shadow: 0 8px 16px rgba(175, 175, 193, 0.16);
    overflow: visible !important;
`;

export const StyledForm = styled.form<{
    onSubmit: (e: FormEvent<HTMLFormElement>) => void;
    children: ReactNode;
}>`
    margin-bottom: 60px;
`;

export const FlexContainer = styled.div`
    display: flex;
    gap: 20px;
    flex-direction: column;
    justify-content: flex-start;
    .default-search-items {
        justify-content: end;
        flex-wrap: wrap;
    }
`;

export const FlexRow = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 40px 80px;
`;


export const FlexRowEnd = styled(FlexRow)`
    justify-items: end;
    align-items: center;
`;

export const StyledSearchControl = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 40px 80px;
`;

export const StyledSearchControlActions = styled.div`
    grid-column: 3 / span 2;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 80px;
    align-items: stretch;

    > div {
        width: 100%;
        min-width: 0;
    }

    > div:only-child {
        grid-column: 2 / -1;
    }

    .usa-button.gi-button {
        width: 100%;
        justify-content: center;
    }
`;

export const FlexItem = styled.div<{ className?: string; highlight?: boolean }>`
    display: flex;
    justify-content: end;
    flex-direction: column;
    width: 100%;

    .gi-input,
    .gi-dropdown {
        margin: 0;
    }
    .gi-dropdown {
        margin-bottom: 0;
        align-items: flex-start;
        flex-direction: column;
        width: 100%;
    }
    .gi-button:active {
        outline: none;
    }
    .gi-input__input {
        width: 100%;
    }
    .gi-dropdown__label {
        width: 100%;
        padding: 0 0 5px;
        display: inherit;
    }
    .gi-dropdown__select {
        width: 100%;
    }
    .gi-datepicker {
        margin-bottom: 0;
    }
    .gi-input__label {
        display: flex;
        padding: 0;
    }
    .react-datepicker__input-container input {
        width: 100%;
    }
    .react-datepicker-wrapper {
        width: 100%;
    }
    button:not([disabled]):focus {
        outline: none;
    }

    .autocomplete-input,
    .autocomplete-menu {
        width: 100%;
    }

    .autocomplete-search-wrapper {
        width: 100%;
        position: relative;
        margin-bottom: 0;
    }
    .autocomplete-menu {
        border: 1px solid ${({ theme }) => theme.gray7};
        border-radius: 0.25rem;
        max-height: 200px;
        overflow-y: auto;
        background-color: ${({ theme }) => theme.defaultWhite};
        box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        position: absolute;
        left: 0;
        right: 0;
        z-index: 10;
    }

    .autocomplete-search-wrapper .autocomplete-input {
        margin-top: 5px;
    }
    ${({ theme, highlight }) =>
        highlight &&
        `
            background: ${theme.infoLightest} !important;
        `}
`;

export const StyledControlButton = styled.div`
    display: flex;
    justify-content: end;
    flex-direction: column;
    width: 220px;
`;

export const StyledAction = styled.button`
    display: flex;
    gap: 5px;
    color: ${({ theme }) => theme.primaryDark};
    border: none;

    .usa-button:not([disabled]):focus {
        outline: none;
    }
    .gi-button {
        svg {
            margin: 0 6px;
        }
    }
`;

export const StyledActionList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 8px 0;
    cursor: pointer;

    li:hover {
        background-color: ${({ theme }) => theme.gray10};
    }
`;

export const StyledPopoverli = styled.li<{ onClick: () => void; children: ReactNode }>`
    color: ${({ theme }) => theme.primaryDark};
    padding: 8px 16px;
    display: flex;
    align-items: center;
    cursor: pointer;
`;

export const StyledActionPopoverIcon = styled(FontAwesomeIcon)`
    margin-right: 8px;
`;

export const StyledModal = styled.div`
    .gi-input {
        text-align: center;
    }
    .gi-modal__content {
        width: 500px;
    }
    .gi-input {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        width: 100%;
    }
    .gi-input input {
        width: 100%;
    }
    .grid-row {
        flex-direction: column;
        width: 100%;
    }
    textarea {
        width: 100%;
        height: 36px;
        resize: none;
        padding: 5px;
    }

    .gi-input__label {
        padding: 0;
    }
    .gi-modal__controls {
        display: flex;
        justify-content: space-between;
    }
    .verify-complete-modal .gi-modal__body {
        display: flex;
        flex-direction: column;
        gap: 8px;
        max-height: 68vh;
    }

    .gi-modal__body label {
        width: 100% !important;
    }
    .verify-complete-modal textarea {
        height: 130px;
    }

    .complete-ticket-modal textarea {
        height: auto;
    }

    .complete-ticket-modal label {
        width: 100%;
    }

    .reassign-ticket-modal .gi-DROPDOWN {
        flex-direction: row;
        justify-content: center;
        gap: 20px;
    }
    .reassign-ticket-modal .gi-modal__body label {
        width: auto !important;
        align-content: center;
    }

    .error-modal .gi-modal__body,
    .error-modal .gi-modal__controls {
        display: flex;
        justify-content: center;
    }

    #addWatchersModal {
        overflow: visible;
        .gi-modal__body {
            overflow: visible;
        }
        .gi-modal__controls {
            justify-content: end;
        }
    }
`;

export const SelectWithIconWrapper = styled.div`
    position: relative;
    width: 100%;

    .select__multi-value__label,
    .select__input {
        font-size: 1.4rem !important;
    }
    .select__value-container--is-multi,
    .select__input-container,
    .select__input,
    .select__control {
        height: 100% !important;
        min-height: 4rem !important;
        margin: 0 !important;
    }
    .select__control {
        padding-left: 30px;
        .select__value-container {
            padding: 0 8px !important;
            .select__input-container {
                padding: 0 !important;
            }
        }
    }
`;

export const SelectIcon = styled(FontAwesomeIcon)`
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    pointer-events: none;
    z-index: 1;
`;


export const StyledInputContainer = styled.div`
    display: flex;
    flex-direction: column;
    > input {
        margin-top: 0px;
        text-align: left;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        /* Remove Firefox spin buttons */
        &[type='number'] {
            -moz-appearance: textfield;
        }
    }

    label {
        font-size: 15px;
        color: ${({ theme }) => theme.gray2};
    }

    .cancelTicketReason {
        height: 60px;
    }
`;

export const StyledCharacterRemaining = styled.div<{ id: string; children: ReactNode }>`
    color: ${({ theme }) => theme.gray2};
    font-size: 14px;
`;

export const StyledError = styled.div`
    color: red;
    font-size: 14px;
    margin-top: 4px;
`;

export const StyledFlexCenter = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const StyledRecordsNotFound = styled(StyledFlexCenter)`
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.primaryDark};
`;

export const AlertContainer = styled.div`
    margin-top: 10px;
    padding: 24px;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.gray8};
    background: ${({ theme }) => theme.defaultWhite};
   
`;

export const StyledConfidentialTicketWarning = styled.div`
    margin-bottom: 16px;
    border-radius: 8px;
    font-size: 14px;
    color: ${({ theme }) => theme.warningDarker};
    .gi-alert__body {
        min-width: 0;
        display: grid;
        grid-template-columns: auto 1fr;
        column-gap: 12px;
        align-items: start;
       
    }
    .gi-alert__text {
        margin: 0;
        grid-column: 2;
        color: ${({ theme }) => theme.warningDarker};
    }
    .confidential-ticket-access-warning__icon {
        grid-row: 1;
        grid-column: 1;
        color: ${({ theme }) => theme.warningMain};
        font-size: 18px;
        line-height: 1.4;
        margin-top: 2px;
    }
    .confidential-ticket-access-warning__icon ~ * {
        grid-column: 2;
    }
`;

export const StyledConfidentialTicketMeta = styled.div`
    font-size: 14px;
    font-weight: 600;
`;

export const StyledConfidentialTicketIds = styled.div<{ $isExpanded: boolean }>`

    margin-top: 4px;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    white-space: ${({ $isExpanded }) => ($isExpanded ? 'normal' : 'nowrap')};
    overflow: ${({ $isExpanded }) => ($isExpanded ? 'visible' : 'hidden')};
    text-overflow: ${({ $isExpanded }) => ($isExpanded ? 'clip' : 'ellipsis')};
    overflow-wrap: ${({ $isExpanded }) => ($isExpanded ? 'anywhere' : 'normal')};
`;

export const StyledConfidentialTicketToggle = styled.button`
    background: transparent;
    border: none;
    color: ${({ theme }) => theme.primaryDark};
    font-weight: 600;
    padding: 0;
    margin-top: 4px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: inherit;
`;

export const StyledAvModal = styled.div`
    .gi-modal__content {
        width: 90vw;
        height: 90vh;
        max-height: 90vh;
        max-width: 90vw;
        margin-top: 5vh;
    }
    .gi-modal__body{
        max-height: 70vh !important;
    }
    .gi-modal__controls {
        display: flex;
        justify-content: flex-end;
        gap: 12px;
        margin-top: -40px;
    }
    textarea {
        width: 100%;
        resize: none;
        padding: 6px 8px;
        min-height: 135px;

        &:disabled {
            background-color: ${({ theme }) => theme.gray7};
            color: ${({ theme }) => theme.gray2};
            cursor: not-allowed;
            opacity: 1;
            -webkit-text-fill-color: ${({ theme }) => theme.gray2};

            &::placeholder {
                color: transparent;
                opacity: 0;
            }
        }
    }
    select:disabled,
    input:disabled {
        background-color: ${({ theme }) => theme.gray7};
        color: ${({ theme }) => theme.gray2};
        cursor: not-allowed;
        opacity: 1;
        -webkit-text-fill-color: ${({ theme }) => theme.gray2};

        &::placeholder {
            color: transparent;
            opacity: 0;
        }
    }
    .gi-modal__body label {
        width: 100% !important;
    }

    .gi-DROPDOWN {
        display: flex;
        flex-direction: column;
        width: 100%;
    }
    .gi-DROPDOWN__select {
        width: 100% !important;
    }
`;


export const StyledAvPanels = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    /* top content | internal comment | external comment — shared across both cards */
    grid-template-rows: auto auto auto;
    column-gap: 20px;
    row-gap: 0;
    align-items: stretch;

    > * {
        min-width: 0;
        display: grid;
        grid-template-rows: subgrid;
        grid-row: span 3;
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        grid-template-rows: none;

        > * {
            display: flex;
            flex-direction: column;
            grid-row: auto;
        }
    }
`;

export const StyledAvCard = styled.div`
    border: 1px solid ${({ theme }) => theme.gray7};
    border-radius: 8px;
    padding: 16px;
    box-sizing: border-box;
    background: ${({ theme }) => theme.defaultWhite};
`;

export const StyledAvMutedCard = styled(StyledAvCard)`
    background: ${({ theme }) => theme.gray10};
`;

export const StyledAvCardTop = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
`;

export const StyledAvFull = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 20px;
`;


export const StyledAvDivider = styled.div`
    border-top: 1px solid ${({ theme }) => theme.gray7};
    margin: 12px -16px;
`;

export const StyledAvPanelTitle = styled.h4`
    font-weight: 700;
    margin: 0 0 12px 0;
`;


export const StyledAvMetaRow = styled.p`
    margin: 4px 0;
`;


export const StyledAvMetaValue = styled.span`
    font-weight: 600;
`;

export const StyledAvCardLabel = styled.p`
    font-weight: 600;
    margin: 10px 0 4px 0;
`;

export const StyledAvFormLabel = styled.label`
    font-weight: 600;
    margin: 10px 0 4px 0;
    display: block;
`;

export const StyledAvCardText = styled.p`
    margin: 0 0 4px 0;
`;

export const StyledAvCardCommentText = styled.p`
    margin: 0 0 4px 0;
    height: 150px;
    overflow-y: auto;
    word-break: break-word;
`;


export const StyledAvComment = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
`;

export const StyledAvField = styled.div`
    margin-bottom: 14px;
`;

export const StyledAvFieldset = styled.fieldset`
    border: 0;
    margin: 0 0 14px 0;
    padding: 0;
    min-width: 0;
`;

export const StyledAvLegend = styled.legend`
    font-weight: 500;
    margin-bottom: 4px;
    padding: 0;
    float: left;
    width: 100%;

    & + * {
        clear: both;
    }

    .req {
        color: ${({ theme }) => theme.dangerDarker};
        margin-left: 2px;
    }
`;

export const StyledAvLabel = styled.span`
    font-weight: 500;
    margin-bottom: 4px;
    display: block;

    .req {
        color: ${({ theme }) => theme.dangerDarker};
        margin-left: 2px;
    }
`;

export const StyledAvPill = styled.span<{ $reject?: boolean; $error?: boolean }>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 16px;
    font-weight: 600;
    width: fit-content;
    background: ${({ theme, $reject, $error }) =>
        $error ? theme.warningLightest : $reject ? theme.dangerLightest : theme.successLightest};
    border: 1px solid ${({ theme, $reject, $error }) =>
        $error ? theme.warningLight : $reject ? theme.dangerLight : theme.successLight};
    ${({ theme, $error }) => $error && `color: ${theme.warningDarker};`}

    svg {
        color: ${({ theme, $reject, $error }) =>
        $error ? theme.warningDarker : $reject ? theme.dangerMain : theme.successMain} !important;
    }
`;

export const StyledAvNote = styled.div<{ $warn?: boolean }>`
    padding: 8px 10px;
    border-radius: 6px;
    margin: 8px 0 0 0;
    border: 1px solid ${({ theme, $warn }) => ($warn ? theme.warningLight : theme.successLight)};
    background: ${({ theme, $warn }) => ($warn ? theme.warningLightest : theme.successLightest)};

    svg {
        margin-right: 6px;
        color: ${({ theme, $warn }) => ($warn ? theme.warningMain : theme.successMain)};
    }
`;

export const StyledAvRadioRow = styled.div`
    display: flex;
    gap: 12px;
`;

export const StyledAvRadioCard = styled.label<{ $selected?: boolean; $disabled?: boolean }>`
    flex: 1 1 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-radius: 8px;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    margin: 0;
    border: 1px solid ${({ theme, $selected }) => ($selected ? theme.successDarker : theme.gray7)};
    background: ${({ theme, $selected }) => ($selected ? theme.successLightest : '#fff')};
    color: ${({ theme, $selected, $disabled }) =>
        $selected ? theme.successDarker : $disabled ? theme.gray1 : 'inherit'};
    transition: border-color 0.15s ease, background 0.15s ease;
    ${({ theme, $selected, $disabled }) =>
        !$selected &&
        !$disabled &&
        css`
            &:hover {
                border-color: ${theme.gray6};
                background: ${theme.gray10};
            }
        `}

    input {
        margin: 0;
        cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
        width: 16px;
        height: 16px;
        min-height: 0;
        flex: 0 0 auto;
    }
`;

export const StyledAvAttachment = styled.a`
    display: block;
    border: 1px solid ${({ theme }) => theme.gray7};;
    border-radius: 6px;
    padding: 8px 12px;
    margin-top: 4px;
    color: ${({ theme }) => theme.successDarker};
    cursor: pointer;
`;
